import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type { OnUpdate } from '@/_schemas/types';
import { resetFormState } from '@/_shared';

import { invalidateCompetitorQuery } from '../apis';
import { competitorFormToUpdate, competitorQueryToForm, fieldNames } from '../converters';
import type { CompetitorFormSchema, CompetitorQuerySchema, CompetitorUpdateSchema } from '../schemas';

const DATES = ['founded_date'];

export function useRevisionHistory(
  form: UseFormReturn<CompetitorFormSchema>,
  onUpdate: OnUpdate<CompetitorUpdateSchema, { data: CompetitorQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof CompetitorFormSchema) => {
    return (
      // field === 'associated_clients' ||
      // field === 'current_web_clients' ||
      field === 'phone_numbers' || field === 'addresses' || field === 'primary_contact' || field === 'logo'
    );
  }, []);

  const onRevert: OnRevert<CompetitorFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV: CompetitorUpdateSchema = {} as CompetitorUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = competitorFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = competitorFormToUpdate({ ...allFormValues });
        nV = { ...nV, [field]: oldValue };
      }

      const res = await onUpdate(nV, 'Competitor changes have been reverted.', true, false);
      if (res.data) {
        if (isPivot) {
          invalidateCompetitorQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<CompetitorQuerySchema, CompetitorFormSchema> = useCallback((q) => {
    return competitorQueryToForm({ ...q });
  }, []);

  const nameConverter: NameConverter<CompetitorFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) return null;
    return fieldNames[f].name;
  }, []);

  const pivotClassifier: PivotClassifier<CompetitorFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;
      if (isPivot) {
        switch (f) {
          case 'phone_numbers':
            changeSetExtractorType = 'PHONE_NUMBER_EMAIL';
            break;
          case 'addresses':
            changeSetExtractorType = 'ADDRESS';
            break;
          case 'primary_contact':
            changeSetExtractorType = 'FIRST_NAME_LAST_NAME';
            break;
          case 'logo':
            changeSetExtractorType = 'MEDIA';
            break;
        }
      }

      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const valueConverter: ValueConverter<CompetitorFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];
    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    )
      return null;

    if (typeof v === 'object') {
      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v, DATES.includes(f));
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  const pivotValueCalculator: PivotValueCalculator<CompetitorFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      if (validatePivot(field)) {
        const nV = competitorFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];

        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });
        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
